import { FC, useEffect, useRef, useState } from 'react';
import { PlayerControlButton } from '../../Player/styles';
import lscache from 'lscache';
import { IconFullscreen, IconSmallScreen } from '../../../icons';
import theme from '../../../theme';
import { lockOrientation, unlockOrientation } from '../../../utils/screenOrientation';
import { useRouter } from 'next/router';

const ControlBarFullScreen: FC<{ fullScreenState: boolean, hideControls: () => void }> = ({
  fullScreenState,
  hideControls,
}) => {
  const router = useRouter();
  const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const [FullScreen, setFullscreen] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(false);
  const element: any = IS_IOS ? document.getElementsByTagName('video')[0] : document.getElementById('custom-player-wrapper');
  const dom: any = document;

  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (document.fullscreenElement) {
      setFullscreen(true);
    } else if (lscache.get('fullscreen') || fullScreenState) {
      handleFullScreen();
    }
    handleMobileBreakpoint();
    window.addEventListener('resize', throttledHandle, { passive: true });

    return () => {
      window.removeEventListener('resize', throttledHandle);
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullScreenState]);

  useEffect(() => {
    if (document) {
      const mobile = Number(theme.breakpoints.md) > Number(window.innerWidth);
      if (lscache.get('fullscreen') && !IS_IOS && mobile) {
        lockOrientation();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.pathname]);

  useEffect(() => {
    window.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      window.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [FullScreen]);

  const handleFullscreenChange = () => {
    lscache.set('fullscreen', FullScreen);
    if (!FullScreen || !document.fullscreenElement) {
      element.setAttribute('data-fullscreen', false);
      setFullscreen(false);
    }
  };

  const throttledHandle = () => {
    if (resizeTimerRef.current !== null) return;
    resizeTimerRef.current = setTimeout(() => {
      handleMobileBreakpoint();
      resizeTimerRef.current = null;
    }, 100);
  };

  const handleMobileBreakpoint = () => {
    setIsMobile(Number(theme.breakpoints.md) > Number(window.innerWidth));
  };

  const orientationChange = (lock: boolean) => {
    if (lock) {
      lockOrientation();
    } else {
      unlockOrientation();
    }
  };

  // requestFullscreen()/exitFullscreen() return Promises that reject when the browser refuses, e.g. the
  // remembered fullscreen at mount has no user gesture. Nothing to recover, but Sentry reports an
  // unhandled rejection, so it is swallowed.
  const handleFullScreen = () => {
    if (FullScreen || document.fullscreenElement) {
      if (dom?.exitFullscreen) {
        dom.exitFullscreen()?.catch(() => undefined);
      } else if (dom?.webkitExitFullscreen) {
        dom.webkitExitFullscreen();
      } else if (dom.mozCancelFullScreen) {
        dom.mozCancelFullScreen();
      } else if (dom.msExitFullscreen) {
        dom.msExitFullscreen();
      }
      element.setAttribute('data-fullscreen', false);
      setFullscreen(false);
      !IS_IOS && isMobile && orientationChange(false);
    } else {
      hideControls();
      if (element.requestFullscreen) {
        element.requestFullscreen()?.catch(() => undefined);
      } else if (element.webkitRequestFullScreen) {
        element.webkitRequestFullScreen();
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      } else if (element.webkitEnterFullscreen) {
        element.webkitEnterFullscreen();
      }
      element.setAttribute('data-fullscreen', true);
      !IS_IOS && setFullscreen(true);
      !IS_IOS && isMobile && orientationChange(true);
    }
  };

  return (
    <PlayerControlButton
      size="semiLarge"
      active={false}
      icon={FullScreen && !IS_IOS ? <IconSmallScreen /> : <IconFullscreen />}
      onClick={handleFullScreen}
    />
  );
};

export default ControlBarFullScreen;
